import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, X, Image, Video } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface FileUploadProps {
  accept: string;
  bucketName: string;
  onUploadComplete: (url: string) => void;
  label: string;
  currentUrl?: string;
  onUrlChange: (url: string) => void;
  placeholder?: string;
  maxSize?: number; // in MB
}

export const FileUpload: React.FC<FileUploadProps> = ({
  accept,
  bucketName,
  onUploadComplete,
  label,
  currentUrl = '',
  onUrlChange,
  placeholder,
  maxSize = 10
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`Ֆայլի չափը չպետք է գերազանցի ${maxSize}MB`);
      return;
    }

    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload file to Supabase Storage
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file);

      if (error) {
        console.error('Upload error:', error);
        toast.error('Ֆայլը ներբեռնելու ժամանակ սխալ է տեղի ունեցել');
        return;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      onUploadComplete(publicUrl);
      onUrlChange(publicUrl);
      toast.success('Ֆայլը հաջողությամբ ներբեռնվեց');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Ֆայլը ներբեռնելու ժամանակ սխալ է տեղի ունեցել');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = () => {
    if (accept.includes('image/')) return <Image className="h-4 w-4" />;
    if (accept.includes('video/')) return <Video className="h-4 w-4" />;
    return <Upload className="h-4 w-4" />;
  };

  const clearFile = () => {
    onUrlChange('');
    setVideoError(false);
    setVideoLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVideoError = () => {
    setVideoError(true);
    setVideoLoading(false);
  };

  const handleVideoLoadStart = () => {
    setVideoLoading(true);
    setVideoError(false);
  };

  const handleVideoLoadedData = () => {
    setVideoLoading(false);
    setVideoError(false);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {/* URL Input */}
      <div className="flex gap-2">
        <Input
          type="url"
          value={currentUrl}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1"
        />
        {currentUrl && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={clearFile}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* File Upload Button */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">կամ</span>
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2"
        >
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              Ներբեռնում...
            </>
          ) : (
            <>
              {getFileIcon()}
              Ներբեռնել ֆայլ
            </>
          )}
        </Button>
      </div>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={accept}
        className="hidden"
        aria-label={label}
      />

      {/* Preview */}
      {currentUrl && (
        <div className="mt-2">
          {accept.includes('image/') && (
            <img
              src={currentUrl}
              alt="Preview"
              className="max-w-xs max-h-32 object-cover rounded border"
            />
          )}
          {accept.includes('video/') && (
            <div className="relative">
              {videoLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded border">
                  <div className="text-white text-sm">Բեռնվում է...</div>
                </div>
              )}
              {videoError ? (
                <div className="flex items-center justify-center bg-gray-100 rounded border max-w-xs h-32">
                  <div className="text-center text-gray-600">
                    <Video className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Տեսանյութը բեռնելու ժամանակ սխալ</p>
                    <button 
                      onClick={() => {
                        setVideoError(false);
                        setVideoLoading(true);
                      }}
                      className="text-xs text-blue-600 hover:underline mt-1"
                    >
                      Կրկին փորձել
                    </button>
                  </div>
                </div>
              ) : (
                <video
                  src={currentUrl}
                  controls
                  preload="metadata"
                  className="max-w-xs max-h-32 rounded border"
                  onError={handleVideoError}
                  onLoadStart={handleVideoLoadStart}
                  onLoadedData={handleVideoLoadedData}
                  onCanPlay={handleVideoLoadedData}
                >
                  Ձեր բրաուզերը չի աջակցում տեսահոլովակների ցուցադրմանը:
                </video>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};